import FormRender from '@/components/Form';
import { IField } from '@/components/Form/data';
import { getQueryUrl } from '@/helpers';
import useIntl from '@/hooks/useIntl';
import User from '@/services/Auth0/User';
import Authentication from '@/services/Authentication';
import { Button, Form, Modal, Spin, message } from 'antd';
import { useState } from 'react';
import { IItem } from '../../components/DangerZone';

interface IChangePassword {
    changePassword: IItem;
    modal: React.FC<{ onOk?: () => void }>;
}

const styles = {
    container: {
        padding: '10px',
        border: '1px solid #ccc',
        borderRadius: '5px',
        backgroundColor: '#f9f9f9',
        marginBottom: 20,
    },
    header: {
        color: '#333',
        fontSize: 16,
    },
    list: {
        listStyleType: 'none',
        paddingLeft: '0',
        fontSize: 12,
    },
    sublist: {
        listStyleType: 'disc',
        paddingLeft: '15px',
    },
    listItem: {
        margin: '5px 0',
    },
    strong: {
        fontWeight: 'bold',
    },
    error: {
        color: 'red',
        marginTop: '5px',
        fontSize: 14,
        fontWeight: 600,
    },
};

const validatePassword = (password: string) => {
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasDigit = /\d/.test(password);
    const hasSpecialChar = /[!$#%]/.test(password);
    const isValidLength = password.length >= 8;

    if (!isValidLength) {
        return 'Fjalëkalimi duhet të jetë të paktën 8 karaktere të gjatë.';
    }
    if (!hasUppercase) {
        return 'Fjalëkalimi duhet të përmbajë të paktën një shkronjë të madhe.';
    }
    if (!hasLowercase) {
        return 'Fjalëkalimi duhet të përmbajë të paktën një shkronjë të vogël.';
    }
    if (!hasDigit) {
        return 'Fjalëkalimi duhet të përmbajë të paktën një numër.';
    }
    if (!hasSpecialChar) {
        return 'Fjalëkalimi duhet të përmbajë të paktën një karakter special (p.sh., !, $, #, %).';
    }
    return '';
};

const ChangePassword = (): IChangePassword => {
    const [show, setShow] = useState<boolean>(false);

    const openPasswordModal = () => {
        setShow(true);
    };

    return {
        changePassword: {
            name: 'Ndrysho fjalëkalimin',
            description: 'Pasi ta ndryshoni atë, përdoruesi nuk do të jetë në gjendje të identifikohet duke përdorur fjalëkalimin e tij të mëparshëm.',
            button: 'Ndrysho',
            event: openPasswordModal,
        },
        modal: ({ onOk }) => {
            const [spinning, setSpinning] = useState<boolean>(false);

            const [formPassword] = Form.useForm();

            const [errorMessage, setErrorMessage] = useState('');

            const t = useIntl();

            const onCancel = () => {
                setShow(false);
                formPassword.resetFields();
            };

            const getUserId = () => {
                return getQueryUrl() || Authentication.getAgencyModel().userId || '';
            };

            const onSave = async () => {
                try {
                    setSpinning(true);

                    const { password } = await formPassword.validateFields();

                    const error = validatePassword(password);

                    if (error) {
                        setErrorMessage(error);
                        return;
                    } else {
                        setErrorMessage('');

                        await User.updatePassword(password, getUserId());

                        message.success(t('success.passwordChanged'));

                        onOk?.();

                        onCancel();
                    }
                } finally {
                    setSpinning(false);
                }
            };

            return (
                <Spin spinning={spinning} tip={t('common.Waiting for the server')}>
                    <Modal
                        title={t('common.updatePassword')}
                        open={show}
                        onOk={onSave}
                        onCancel={onCancel}
                        destroyOnClose
                        centered
                        footer={[
                            <Button key="back" onClick={onCancel}>
                                {t('common.cancel')}
                            </Button>,
                            <Button loading={spinning} key="submit" type="primary" onClick={onSave}>
                                {t('common.save')}
                            </Button>,
                        ]}
                    >
                        <>
                            <div style={styles.container}>
                                <ul style={styles.list}>
                                    <li style={styles.listItem}>
                                        Fjalëkalimi duhet të jetë të paktën <span style={styles.strong}>tetë karaktere të gjatë</span>.
                                    </li>
                                    <li style={styles.listItem}>
                                        Ai duhet të përfshijë karaktere nga <span style={styles.strong}>të katër kategoritë</span>:
                                    </li>
                                    <ul style={styles.sublist}>
                                        <li style={styles.listItem}>
                                            <span style={styles.strong}>Shkronja të mëdha latine</span> (A deri në Z)
                                        </li>
                                        <li style={styles.listItem}>
                                            <span style={styles.strong}>Shkronja të vogla latine</span> (a deri në z)
                                        </li>
                                        <li style={styles.listItem}>
                                            <span style={styles.strong}>Numra bazë 10</span> (0 deri në 9)
                                        </li>
                                        <li style={styles.listItem}>
                                            <span style={styles.strong}>Karaktere jo-alfanumerike</span> si:
                                        </li>
                                        <ul style={styles.list}>
                                            <li style={styles.listItem}>pika thirrëse (!)</li>
                                            <li style={styles.listItem}>shenja dollarike ($)</li>
                                            <li style={styles.listItem}>shenja numri (#)</li>
                                            <li style={styles.listItem}>përqindje (%)</li>
                                        </ul>
                                    </ul>
                                </ul>
                            </div>
                            <Form scrollToFirstError name="formPassword" autoComplete="off" layout="vertical" form={formPassword}>
                                <FormRender
                                    formFields={
                                        [
                                            {
                                                component: 'InputPassword',
                                                name: 'password',
                                                label: 'Password',
                                                placeholder: 'Password',
                                                rules: [{ required: true, message: 'errors.required.password' }],
                                                span: 24,
                                                hasFeedback: true,
                                            },
                                            {
                                                component: 'InputPassword',
                                                name: 'confirm',
                                                label: 'Confirm Password',
                                                placeholder: 'Confirm Password',
                                                dependencies: ['password'],
                                                rules: [{ required: true, message: 'errors.required.confirm_password' }],
                                                span: 24,
                                                hasFeedback: true,
                                            },
                                        ] as IField[]
                                    }
                                />
                            </Form>
                            {errorMessage && <div style={styles.error}>{errorMessage}</div>}
                        </>
                    </Modal>
                </Spin>
            );
        },
    };
};

export default ChangePassword;
